import { Box, Grid, useMediaQuery } from '@material-ui/core';
import { createStyles, makeStyles, Theme, useTheme } from '@material-ui/core/styles';
import React from 'react';
import { UIBlockElement } from 'common-ts/dist/models/Product';
import { RenderBlockElement } from './PageComponentElementBlocks';

// Renders a product layout inside the page frame.
//
// A flat UIBlockElement[] can't express the desktop product page, which is
// a 70/30 split: media on the left, everything the shopper reads and clicks
// on the right. So the split stays as page CHROME, and each block carries a
// `fullWidth` flag deciding which side of it the block belongs to:
//
//   desktop:  [ gallery (70%) | rail blocks (30%) ]
//             [ full-width blocks, stacked below  ]
//   mobile:   one column, blocks in plain layout order (fullWidth ignored)
//
// Shared by the real product page and the dashboard preview iframe so the
// two cannot drift.

const useStyles = makeStyles((theme: Theme) =>
createStyles({
  main: {},
  // Same 70% as the old sectionPhoto, but it must NOT vanish on mobile
  // (that class was display:none below xs because the page rendered a
  // separate mobile carousel elsewhere). The gallery block now renders both
  // variants itself, so here the column simply goes full width.
  photoColumn: {
    margin: '0',
    boxSizing: 'border-box',
    flex: '0 0 auto',
    flexGrow: 0,
    maxWidth: '70%',
    flexBasis: '70%',
    [theme.breakpoints.down('xs')]: {
      maxWidth: '100%',
      flexBasis: '100%',
    },
  },
  railColumn: {
    margin: '0',
    boxSizing: 'border-box',
    flex: '0 0 auto',
    flexGrow: 0,
    maxWidth: '30%',
    flexBasis: '30%',
    paddingLeft: '26px',
    paddingRight: '30px',
    [theme.breakpoints.down('xs')]: {
      paddingLeft: '20px',
      paddingRight: '20px',
      maxWidth: '100%',
      flexBasis: '100%',
    },
  },
}),
);

function codeFor(block: any, isDesktop: boolean): string {
  return isDesktop ? block?.elementDesktopCode : block?.elementMobileCode;
}

function contentFor(block: any, isDesktop: boolean): any {
  return isDesktop ? block?.elementDesktopContentJson : block?.elementMobileContentJson;
}

// contentJson may arrive as a JSON string (the block renderer tolerates
// both), so fullWidth has to be read defensively.
function isFullWidth(content: any): boolean {
  if (typeof content === 'string') {
    try { return !!JSON.parse(content)?.fullWidth; } catch { return false; }
  }
  return !!content?.fullWidth;
}

export default function ProductPageBody({ layout }: { layout: UIBlockElement[] }) {
  const classes = useStyles();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('sm'));

  const render = (block: UIBlockElement, key: string) => (
    <RenderBlockElement
      key={key}
      code={codeFor(block, isDesktop)}
      contentJson={contentFor(block, isDesktop)} />
  );

  // normalizeProductLayout guarantees the gallery exists at index 0.
  const gallery = layout[0];
  const rest = layout.slice(1);

  if (!isDesktop) {
    return (
      <Box className={classes.main}>
        <Grid container>
          <Box className={classes.photoColumn}>
            {gallery && render(gallery, 'block-0')}
          </Box>
          <Box className={classes.railColumn}>
            {rest.map((block, i) => render(block, 'block-' + (i + 1)))}
          </Box>
        </Grid>
      </Box>
    );
  }

  const rail: { block: UIBlockElement; index: number }[] = [];
  const wide: { block: UIBlockElement; index: number }[] = [];
  rest.forEach((block, i) => {
    const target = isFullWidth(contentFor(block, true)) ? wide : rail;
    target.push({ block, index: i + 1 });
  });

  return (
    <>
      <Box className={classes.main}>
        <Grid container>
          <Box className={classes.photoColumn}>
            {gallery && render(gallery, 'block-0')}
          </Box>
          <Box className={classes.railColumn}>
            {rail.map(({ block, index }) => render(block, 'block-' + index))}
          </Box>
        </Grid>
      </Box>
      {wide.map(({ block, index }) => render(block, 'block-' + index))}
    </>
  );
}
